import { useState, useEffect } from "react";
import {
  RadioButton,
  RadioButtonChangeEvent,
} from "@progress/kendo-react-inputs";
import {
  ComboBox,
  ComboBoxChangeEvent,
  ComboBoxFilterChangeEvent,
} from "@progress/kendo-react-dropdowns";
import { Button } from "@progress/kendo-react-buttons";
import { CohortResult, Schedule } from "@mikros/library";
import { Error } from "@progress/kendo-react-labels";
import { FormattedMessage, useIntl } from "react-intl";
import { filterBy } from "@progress/kendo-data-query";

export interface UserCohortProps {
  schedule: Schedule;
  onStepperChange: (schedule: Schedule) => void;
  cohortResult: CohortResult;
  backStepper: () => void;
}

export function UserCohort(props: UserCohortProps) {
  const intl = useIntl();

  const [isNewUserAllowed, setIsNewUserAllowed] = useState<string>(
    props.schedule.isNewUserAllowed
  );
  const [enrolmntType, setEnrolmntType] = useState<string>(
    props.schedule.enrolmntType
  );
  const [cohortName, setCohortName] = useState<string>();
  const [selectedCohortOption, setSelectedCohortOption] = useState<string>();
  const [addNewUserOption, setAddNewUserOption] = useState<String>(
    "NEW_USERS_STRT_FIRST"
  );

  const [errors, setErrors] = useState<any>({});
  const [notification, setNotification] = useState<boolean>(false);
  const [cohortData, setCohortData] = useState<string[]>([]);
  const [saveButtonClicked, setSaveButtonClicked] = useState<boolean>(false);

  const handleIsNewUserAllowed = (e: string) => {
    setIsNewUserAllowed(e);
  };

  const handleEnrolmentType = (e: RadioButtonChangeEvent) => {
    setEnrolmntType(e.value);
  };

  const handleCohortName = (e: ComboBoxChangeEvent) => {
    setCohortName(e.value);
  };

  const handleCohortOption = (e: RadioButtonChangeEvent) => {
    console.log("handle cohort option value", e.value);
    setSelectedCohortOption(e.value);
    handleIsNewUserAllowed(e.value);
  };
  const handleSecondCohortOption = (e: RadioButtonChangeEvent) => {
    setSelectedCohortOption(e.value);
    handleIsNewUserAllowed("NEW_USERS_STRT_FIRST");
  };
  const handleAddNewUserOption = (e: RadioButtonChangeEvent) => {
    console.log("entered handle add new user option", e.value);
    setAddNewUserOption(e.value);
    handleIsNewUserAllowed(e.value);
  };

  const validate = () => {
    if (enrolmntType !== undefined && cohortName !== undefined) {
      return true;
    }

    if (enrolmntType === "COHORT") {
      return true;
    }

    let err: any = {};

    if (enrolmntType === undefined) {
      err.enrolmntType = {
        error: intl.formatMessage({ id: "schedule.course.selectOption" }),
      };
    }
    if (cohortName === undefined) {
      err.cohortName = {
        error: intl.formatMessage({ id: "schedule.userCohort.selectCohort" }),
      };
    }

    if (selectedCohortOption === "second") {
      console.log("THe cohort option is second");
      err.cohortOption = {
        error: intl.formatMessage({ id: "schedule.userCohort.selectOption" }),
      };
    }

    if (selectedCohortOption === undefined) {
      console.log("THe cohort option is undefined");
      err.cohortOption = {
        error: intl.formatMessage({
          id: "schedule.userCohort.selectCohortOption",
        }),
      };
    }

    if (isNewUserAllowed === undefined) {
      err.cohortOption = {
        error: intl.formatMessage({
          id: "schedule.deliverySchedule.selectOption",
        }),
      };
    }

    setErrors(err);
    return false;
  };

  const handleCohortDataChange = () => {
    if (validate()) {
      if (enrolmntType !== "ALL_COHORT_USER") {
        setNotification(true);
        return;
      }
      let selectedCohort = props.cohortResult.resources?.find((element) => {
        if (element.cohortName === cohortName) {
          return element;
        }
      });

      let schedule: Schedule = props.schedule;
      schedule.enrolmntType = enrolmntType!!;
      schedule.cohortName = cohortName!!;
      if (isNewUserAllowed !== "second") {
        schedule.isNewUserAllowed = isNewUserAllowed!!;
      }
      // schedule.cohortId = selectedCohort!!.cohortId;
      schedule.cohortId = selectedCohort!!.cohortId!!;

      // schedule.cohortuserCnt = selectedCohort!!.cohortuserCnt;
      schedule.cohortuserCnt = selectedCohort?.cohortuserCnt!!;
      props.onStepperChange(schedule);
    }
  };

  useEffect(() => {
    setEnrolmntType(props.schedule.enrolmntType);
    setCohortName(props.schedule.cohortName);
    if (props.schedule.isNewUserAllowed === "NEW_USERS_NO") {
      setSelectedCohortOption(props.schedule.isNewUserAllowed);
    } else if (props.schedule.isNewUserAllowed === "NEW_USERS_STRT_FIRST") {
      setSelectedCohortOption("second");
      setAddNewUserOption(props.schedule.isNewUserAllowed);
    } else if (props.schedule.isNewUserAllowed === "NEW_USERS_STRT_CURRENT") {
      setSelectedCohortOption("second");
      setAddNewUserOption("NEW_USERS_STRT_CURRENT");
    } else {
      setAddNewUserOption(props.schedule.isNewUserAllowed);
    }
  }, []);

  useEffect(() => {
    if (props.cohortResult) {
      setCohortData(
        props.cohortResult.resources?.map((element, index) => {
          return element.cohortName;
        })
      );
    }
  }, [props.cohortResult]);

  const handleFilterChange = (e: ComboBoxFilterChangeEvent) => {
    const data = filterBy(
      props.cohortResult.resources?.map((element, index) => {
        return element.cohortName;
      }),
      e.filter
    );
    setCohortData(data);
  };

  return (
    <div className="w-full h-full overflow-auto space-y-8">
      <div className="w-full">
        <h4 className="font-bold">
          <FormattedMessage id="schedule.createSchedule.cohortEnrolled" />
        </h4>
        <Error>{errors.enrolmntType?.error}</Error>
        <div className="ps-20 pt-2 flex items-center">
          <RadioButton
            name="group1"
            value="ALL_COHORT_USER"
            checked={enrolmntType === "ALL_COHORT_USER"}
            label={intl.formatMessage({
              id: "schedule.createSchedule.cohortEnrolledOption1",
            })}
            onChange={handleEnrolmentType}
          />
        </div>
        <div className="ps-20 pt-2 flex items-center">
          <RadioButton
            name="group1"
            value="COHORT"
            disabled={true}
            checked={enrolmntType === "COHORT"}
            label={intl.formatMessage({
              id: "schedule.createSchedule.cohortEnrolledOption2",
            })}
            onChange={handleEnrolmentType}
          />
        </div>
        <div className="ps-20 pt-2 flex items-center">
          <RadioButton
            name="group1"
            value="COHORT_USER"
            disabled={true}
            checked={enrolmntType === "COHORT_USER"}
            label={intl.formatMessage({
              id: "schedule.createSchedule.cohortEnrolledOption3",
            })}
            onChange={handleEnrolmentType}
          />
        </div>
      </div>
      <div
        className={enrolmntType === "COHORT" ? "hidden" : "flex-cols space-y-8"}
      >
        <div className="w-full">
          <h4 className="font-bold">
            <FormattedMessage id="schedule.createSchedule.cohortSelect" />{" "}
          </h4>
          <Error>{errors.cohortName?.error}</Error>
          <div className="ps-20 pt-2">
            <ComboBox
              filterable={true}
              onFilterChange={(e: ComboBoxFilterChangeEvent) => {
                handleFilterChange(e);
              }}
              data={cohortData}
              onChange={handleCohortName}
              className="w-56 md:w-96"
              value={cohortName}
              placeholder={intl.formatMessage({
                id: "schedule.userCohort.selectCohortPlaceholder",
              })}
            />
          </div>
        </div>
        <div className="w-full">
          <h4 className="font-bold">
            <FormattedMessage id="schedule.createSchedule.cohortOptions" />
          </h4>
          <div className="ps-20 pt-2 flex items-center">
            <RadioButton
              name="group2"
              value="NEW_USERS_NO"
              checked={selectedCohortOption === "NEW_USERS_NO"}
              label={intl.formatMessage({
                id: "schedule.createSchedule.cohortOption1",
              })}
              onChange={handleCohortOption}
            />
          </div>
          <div className="ps-20 pt-2 flex items-center">
            <RadioButton
              name="group2"
              value="second"
              checked={selectedCohortOption === "second"}
              label={intl.formatMessage({
                id: "schedule.createSchedule.cohortOption2",
              })}
              onChange={handleSecondCohortOption}
            />
          </div>
          <div
            className={selectedCohortOption === "second" ? "ps-36" : "hidden"}
          >
            <div className="flex  pt-2 items-center">
              <RadioButton
                name="newUser"
                value="NEW_USERS_STRT_FIRST"
                checked={addNewUserOption === "NEW_USERS_STRT_FIRST"}
                label={intl.formatMessage({
                  id: "schedule.createSchedule.cohortOption3",
                })}
                onChange={handleAddNewUserOption}
              />{" "}
              {/* <p className="text-mk-grey text-xs flex items-end ps-1">
                <FormattedMessage id="schedule.userCohort.recommended" />
              </p> */}
            </div>

            <div
              className={
                props.schedule.isUserRespReqrd
                  ? "hidden"
                  : "pt-2 flex items-center"
              }
            ></div>
          </div>
        </div>
      </div>
      <div className="flex justify-end space-x-4 w-full pb-[15%] pr-[25%]">
        <Button themeColor={"primary"} onClick={() => props.backStepper()}>
          <FormattedMessage id="backButton" />
        </Button>
        <Button
          themeColor={"primary"}
          onClick={() => {
            setSaveButtonClicked(true);
            handleCohortDataChange();
          }}
        >
          <FormattedMessage id="nextButton" />
        </Button>
      </div>
      {/* <div className="pb-[10%]"></div> */}
    </div>
  );
}
